import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Card,
  CardContent,
  Grid,
  CardActions,
  Button,
  Divider,
  CardHeader,
  IconButton,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import InputV2 from "./InputV2";
import MalhaSelectV2 from "./MalhaSelectV2";
import GerenciaSelectV2 from "./GerenciaSelectV2";
import PoloSelectV2 from "./PoloSelectV2";
import SubestacaoSelectV2 from "./SubestacaoSelectV2";
import AlimentadorSelectV2 from "./AlimentadorSelectV2";
import NsirStatusSelectV2 from "./NsirStatusSelectV2";
import UsuarioSelectV2 from "./UsuarioSelectV2";
import DatePickerV2 from "./DatePickerV2";
import TrueFalseSelectV2 from "./TrueFalseSelectV2";
import MultiSelectV2 from "./MultiSelectV2";
import DateTypeSelectV2 from "./DateTypeSelectV2";
import DefaultSelectV2 from "./DefaultSelectV2";
import OptionSelectV2 from "./OptionSelectV2";
import NsirSelectV2 from "./NsirSelectV2";
import EquipamentoSelectV2 from "./EquipamentoSelectV2";
import SearchSelectV2 from "./SearchSelectV2";
import ValidateSelectV2 from "./ValidateSelectV2";
import CartaoSelectV2 from "./CartaoSelectV2";
import VeiculoSelectV2 from "./VeiculoSelectV2";
import TipoEquipamentoSelectV2 from "./TipoEquipamentoSelectV2";
import NumberInputV2 from "./NumberInputV2";
import MultiSelectOptionsV2 from "./MultiSelectOptionsV2";
import MunicipioSelectV2 from "./MunicipioSelectV2";

import * as selectorsAuth from "../../store/auth/reducer";
import * as selectors from "../../store/filtro/reducer";
import * as actions from "../../store/filtro/actions";
import * as selectorsProduto from "../../store/produto/reducer";
import { loadProdutosOptions } from "../../store/produto/actions";
import { loadPedidoFornecedorOptions } from "../../store/pedido/actions";
import * as selectorsPedido from "../../store/pedido/reducer";
import * as Role from "../../constants/role";
import moment from "moment";
import { tipoServicoTemSubtipo } from "../../utils";

/** Cópia rasa do filtro — descarta chaves extras ao limpar. */
const cloneFilter = (filter) => {
  if (!filter || typeof filter !== "object") {
    return {};
  }
  return { ...filter };
};

const mapFornecedorOptions = (items) =>
  (items || []).map(({ value }) => ({ value, label: value }));

const fieldRenderers = {
  useDates: ({ filter, setFilter, md = 2, xs = 6, useDateTypes, useDateTypesReadOnly }) => (
    <>
      <Grid size={{ md, xs }}>
        <DatePickerV2
          label="Data Início"
          onChange={(ts) => setFilter({ ...filter, inicio: ts })}
          value={filter.inicio}
        />
      </Grid>
      <Grid size={{ md, xs }}>
        <DatePickerV2
          label="Data Fim"
          onChange={(ts) => setFilter({ ...filter, fim: ts })}
          value={filter.fim}
        />
      </Grid>
      {useDateTypes ? (
        <Grid size={{ md: 8, xs: 12 }}>
          <DateTypeSelectV2
            label="Filtro de datas"
            onSelect={(dateTypes) => setFilter({ ...filter, dateTypes })}
            value={filter.dateTypes}
            disabled={useDateTypesReadOnly}
          />
        </Grid>
      ) : (
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
          }}
        />
      )}
    </>
  ),

  useCodigo: ({ filter, setFilter, codigoLabel, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={codigoLabel || "Código"}
        onChange={(e) => setFilter({ ...filter, codigo: e.target.value })}
        value={filter.codigo}
      />
    </Grid>
  ),

  useFilterInput: ({ filter, setFilter, filterKey, label, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={label}
        onChange={(e) => setFilter({ ...filter, [filterKey]: e.target.value })}
        value={filter[filterKey] || ""}
      />
    </Grid>
  ),

  useEquipamento: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Equipamento"
        onChange={(e) => setFilter({ ...filter, equipamento: e.target.value })}
        value={filter.equipamento}
      />
    </Grid>
  ),

  useUsuarios: ({
    filter,
    setFilter,
    multiSelect,
    label = 'Usuários',
    grupos,
    disabled,
    md = 2,
    xs = 6,
    key: filterFieldKey = 'usuarioId',
    regimeTrabalhoIds,
    ativo,
  }) => (
    <Grid size={{ md, xs }}>
      <UsuarioSelectV2
        label={label}
        onSelect={(val) =>
          setFilter({
            ...filter,
            [filterFieldKey]: val,
          })}
        multiSelect={multiSelect}
        value={filter[filterFieldKey]}
        grupos={grupos}
        disabled={disabled}
        regimeTrabalhoIds={regimeTrabalhoIds}
        ativo={ativo}
      />
    </Grid>
  ),

  useRegimeTrabalho: ({
    filter,
    setFilter,
    md = 6,
    xs = 12,
    key = 'regimeTrabalhoIds',
    label = 'Regime de Trabalho',
    clearUsuarioKey = 'usuarioId',
  }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo="USUARIO_REGIME_TRABALHO"
        multiSelect
        onSelect={(values) =>
          setFilter({
            ...filter,
            [key]: values,
            ...(clearUsuarioKey ? { [clearUsuarioKey]: [] } : {}),
          })}
        value={filter[key]}
      />
    </Grid>
  ),

  useInspetor: ({ filter, setFilter, multiSelect, userRole, md = 2, xs = 6, disabled = false }) =>
    Role.INSPETOR !== userRole && (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Inspetor"
          onSelect={(inspetorId) => setFilter({ ...filter, inspetorId })}
          multiSelect={multiSelect}
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={filter.inspetorId}
          disabled={disabled}
        />
      </Grid>
    ),

  useInspetorAuxiliar: ({ filter, setFilter, multiSelect, userRole, md = 2, xs = 6 }) =>
    Role.INSPETOR !== userRole && (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Inspetor Auxiliar"
          onSelect={(inspetorAuxiliarId) =>
            setFilter({ ...filter, inspetorAuxiliarId })
          }
          multiSelect={multiSelect}
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={filter.inspetorAuxiliarId}
        />
      </Grid>
    ),

  useDigitador: ({ filter, setFilter, disabled = false, md = 12, xs = 12, multiSelect }) => {
    return (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Digitador(es)"
          onSelect={digitadorId => setFilter({ ...filter, digitadorId })}
          grupos={[Role.ADMIN, Role.DIGITADOR]}
          value={filter.digitadorId}
          disabled={disabled}
          multiSelect={multiSelect}
        />
      </Grid>
    )
  },

  useMalha: ({ filter, setFilter, md = 3, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <MalhaSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            malhaId: value,
            gerenciaId: null,
            poloId: null,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={filter.malhaId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  useMunicipio: ({
    filter,
    setFilter,
    md = 12,
    xs = 12,
    multiSelect = true,
    filterKey = 'municipioIbge',
    key = 'codigoIbge',
    nomeKey = 'municipioNome',
    label = 'Cidades',
  }) => {
    const valueKey = multiSelect ? filterKey : key;

    return (
      <Grid size={{ md, xs }}>
        <MunicipioSelectV2
          label={label}
          multiSelect={multiSelect}
          value={filter[valueKey]}
          nome={multiSelect ? undefined : filter[nomeKey]}
          uf={multiSelect ? undefined : filter.uf}
          onSelect={(selected) => {
            if (multiSelect) {
              setFilter({ ...filter, [filterKey]: selected });
              return;
            }

            setFilter({
              ...filter,
              [key]: selected ? selected.codigoIbge : null,
              [nomeKey]: selected ? selected.nomeOriginal : null,
            });
          }}
        />
      </Grid>
    );
  },

  useGerencia: ({ filter, setFilter, md = 2, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <GerenciaSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            gerenciaId: value,
            poloId: null,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={filter.gerenciaId}
        malhaId={filter.malhaId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  usePolo: ({ filter, setFilter, md = 3, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <PoloSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            poloId: value,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={filter.poloId}
        malhaId={filter.malhaId}
        gerenciaId={filter.gerenciaId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  useSubestacao: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <SubestacaoSelectV2
        onSelect={(value) =>
          setFilter({ ...filter, subestacaoId: value, alimentadorId: null })
        }
        value={filter.subestacaoId}
        malhaId={filter.malhaId}
        gerenciaId={filter.gerenciaId}
        poloId={filter.poloId}
      />
    </Grid>
  ),

  useAlimentador: ({ filter, setFilter, md = 2, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <AlimentadorSelectV2
        onSelect={(value) => setFilter({ ...filter, alimentadorId: value })}
        value={filter.alimentadorId}
        malhaId={filter.malhaId}
        gerenciaId={filter.gerenciaId}
        poloId={filter.poloId}
        subestacaoId={filter.subestacaoId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  usePossuiCroqui: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Croqui"
        onChange={(e) => setFilter({ ...filter, possuiCroqui: e.target.value })}
        value={filter.possuiCroqui}
      />
    </Grid>
  ),

  useNsirPagaInspetor: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Nsir Paga Inspetor"
        onChange={(e) =>
          setFilter({ ...filter, nsirPagaInspetor: e.target.value })
        }
        value={filter.nsirPagaInspetor}
      />
    </Grid>
  ),

  useCliente: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Clientes"
        codigo="CLIENTE"
        multiSelect
        onSelect={(values) => setFilter({ ...filter, clientes: values })}
        value={filter.clientes}
      />
    </Grid>
  ),

  useNsirStatus: ({
    filter,
    setFilter,
    customStatus,
    md = 12,
    xs = 12,
    multiSelect = true,
    filterKey = 'statuses',
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <NsirStatusSelectV2
        onSelect={(value) => setFilter({ ...filter, [filterKey]: value })}
        value={filter[filterKey]}
        isMultiple={multiSelect}
        options={customStatus}
        disabled={disabled}
      />
    </Grid>
  ),

  useTipoServico: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    multiSelect = true,
    label = 'Tipo de Serviços',
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo="TIPO_SERVICO"
        multiSelect={multiSelect}
        onSelect={tipoServicos => setFilter({ ...filter, tipoServicos, subTipoIds: null })}
        value={filter.tipoServicos}
        disabled={disabled}
      />
    </Grid>
  ),

  useCtPlan: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="CT. Plan"
        onChange={(e) => setFilter({ ...filter, ctPlan: e.target.value })}
        value={filter.ctPlan || ''}
      />
    </Grid>
  ),

  useCtExec: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="CT. Exec"
        onChange={(e) => setFilter({ ...filter, ctExec: e.target.value })}
        value={filter.ctExec || ''}
      />
    </Grid>
  ),

  useApro: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <NumberInputV2
        label="APRO"
        name="apro"
        onChange={(valor) => setFilter({ ...filter, apro: valor || null })}
        value={filter.apro}
        decimalScale={0}
        prefix=""
        thousandSeparator=""
      />
    </Grid>
  ),

  useHd: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <NumberInputV2
        label="HD"
        name="hd"
        onChange={(valor) => setFilter({ ...filter, hd: valor || null })}
        value={filter.hd}
        decimalScale={0}
        prefix=""
        thousandSeparator=""
      />
    </Grid>
  ),

  usePossuiHd: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui HD"
        onChange={(e) => setFilter({ ...filter, possuiHd: e.target.value || null })}
        value={filter.possuiHd}
      />
    </Grid>
  ),

  usePossuiApro: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Apro"
        onChange={(e) => setFilter({ ...filter, possuiApro: e.target.value || null })}
        value={filter.possuiApro}
      />
    </Grid>
  ),

  useNumNsmp: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Nº Nsmp"
        onChange={(e) => setFilter({ ...filter, numNsmp: e.target.value })}
        value={filter.numNsmp || ''}
      />
    </Grid>
  ),

  usePrioridade: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Prioridade"
        onChange={(e) => setFilter({ ...filter, prioridade: e.target.value })}
        value={filter.prioridade || ''}
      />
    </Grid>
  ),

  useAdere: ({ filter, setFilter }) => (
    <>
      <Grid size={{ md: 2, xs: 6 }}>
        <TrueFalseSelectV2
          label="Adere"
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'false') {
              setFilter({ ...filter, possuiAdere: value, adereMonths: null });
            } else {
              setFilter({ ...filter, possuiAdere: value });
            }
          }}
          value={filter.possuiAdere}
        />
      </Grid>
      <Grid size={{ md: 3, xs: 6 }}>
        <MultiSelectOptionsV2
          label="Adere Meses"
          url="nsirs/options/aderes"
          onSelect={(values) => setFilter({ ...filter, adereMonths: values })}
          value={filter.adereMonths}
          disabled={filter.possuiAdere === 'false'}
        />
      </Grid>
    </>
  ),

  useSubTipoServico: ({ filter, setFilter, md = 2, xs = 6, multiSelect = true }) => {
    const parentIds = (filter.tipoServicos || []).filter(tipoServicoTemSubtipo);
    const hasSubtipoTipo = parentIds.length > 0;

    return (
      <Grid size={{ md, xs }}>
        <OptionSelectV2
          label="Subtipo de Serviços"
          codigo="NSIR_SUBTIPOS"
          multiSelect={multiSelect}
          parentIds={hasSubtipoTipo ? parentIds : undefined}
          disabled={!hasSubtipoTipo}
          onSelect={subTipoIds => setFilter({ ...filter, subTipoIds })}
          value={filter.subTipoIds}
        />
      </Grid>
    );
  },

  useClientes: ({ filter, setFilter, md = 2, xs = 6, multiSelect = true }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Clientes"
        codigo="CLIENTE"
        multiSelect={multiSelect}
        onSelect={clienteIds => setFilter({ ...filter, clienteIds })}
        value={filter.clienteIds}
      />
    </Grid>
  ),

  useOption: ({ filter, setFilter, code, label, key, md = 2, xs = 6, multiSelect = true }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo={code}
        multiSelect={multiSelect}
        onSelect={(values) => setFilter({ ...filter, [key]: values })}
        value={filter[key]}
      />
    </Grid>
  ),

  useMultiSelectCustomOption: ({ filter, setFilter, options, label, key, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <MultiSelectV2
        label={label}
        onChange={values => setFilter({ ...filter, [key]: values ? values.map(v => v.value) : [] })}
        defaultValue={
          options && filter[key] && filter[key].length
            ? options.filter(o => filter[key].indexOf(o.value) > -1)
            : []
        }
        options={options}
      />
    </Grid>
  ),

  useMesReferencia: ({ filter, setFilter, md = 2, xs = 6, label = "Mês Referência", key = "mesReferencia" }) => (
    <Grid size={{ md, xs }}>
      <DatePickerV2
        label={label}
        yearMonth
        showInput
        onChange={(data) => setFilter({ ...filter, [key]: data ? moment(data).format('YYYY-MM') : null })}
        value={filter[key] ? moment(filter[key], 'YYYY-MM').valueOf() : null}
      />
    </Grid>
  ),

  useNsirs: ({ filter, setFilter, label = "Nsirs", key = "nsirIds", md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <NsirSelectV2
        label={label}
        onSelect={(values) => setFilter({ ...filter, [key]: values })}
        value={filter[key]}
      />
    </Grid>
  ),

  useSelectOption: ({ filter, setFilter, md = 2, xs = 6, options, key, label }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label={label}
        name={key}
        showEmpty
        options={options}
        onSelect={e => setFilter({ ...filter, [key]: e.target.value || null })}
        value={filter[key]}
      />
    </Grid>
  ),

  useSearchSelect: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    options,
    key,
    label,
    multiSelect = false,
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
        disabled={disabled}
      />
    </Grid>
  ),

  useVeiculo: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    key = "veiculoIds",
    label = "Veículos",
    multiSelect = true,
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <VeiculoSelectV2
        label={label}
        multiSelect={multiSelect}
        disabled={disabled}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
      />
    </Grid>
  ),

  useCategoriaVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label="Categorias"
        multiSelect
        onSelect={(categoriaVeiculos) => setFilter({ ...filter, categoriaVeiculos })}
        options={[
          { value: 'CARRO', label: 'Carro' },
          { value: 'CAMINHAO', label: 'Caminhão' },
          { value: 'UTILITARIO_CAMINHONETE', label: 'Caminhonete' },
          { value: 'MAQUINA', label: 'Máquina' },
          { value: 'MOTO', label: 'Moto' },
          { value: 'REBOQUE', label: 'Reboque' },
        ]}
        value={filter.categoriaVeiculos}
      />
    </Grid>
  ),

  useTipoVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label="Proprietários"
        multiSelect
        onSelect={(tipoVeiculos) => setFilter({ ...filter, tipoVeiculos })}
        options={[
          { value: 'ELETROPONTES', label: 'Eletropontes' },
          { value: 'PARTICULAR', label: 'Particular' },
          { value: 'ALUGADO', label: 'Alugado' },
        ]}
        value={filter.tipoVeiculos}
      />
    </Grid>
  ),

  useFinalidadeVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Finalidades"
        codigo="VEICULO_FINALIDADES"
        multiSelect
        onSelect={(finalidades) => setFilter({ ...filter, finalidades })}
        value={filter.finalidades}
      />
    </Grid>
  ),

  useAtivo: ({ filter, setFilter, md = 2, xs = 6, useAtivoLabel }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label={useAtivoLabel || 'Ativo'}
        onChange={(e) => setFilter({ ...filter, ativo: e.target.value || null })}
        value={filter.ativo}
      />
    </Grid>
  ),

  useRole: ({ filter, setFilter, md = 3, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label="Função"
        showEmpty
        options={[
          { id: 'ROLE_ADMIN', nome: 'Administrador' },
          { id: 'ROLE_CROQUI', nome: 'Croqui' },
          { id: 'ROLE_DIGITADOR', nome: 'Digitador' },
          { id: 'ROLE_INSPETOR', nome: 'Inspetor' },
          { id: 'ROLE_AUDITORIA_NSIR', nome: 'Auditoria NSIR' },
        ]}
        onSelect={(e) => setFilter({ ...filter, role: e.target.value || null })}
        value={filter.role}
      />
    </Grid>
  ),

  useSetor: ({ filter, setFilter, md = 3, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Setor"
        codigo="USUARIO_SETOR"
        onSelect={(setorId) => setFilter({ ...filter, setorId })}
        value={filter.setorId}
      />
    </Grid>
  ),

  usePossuiCarteira: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Carteira"
        onChange={(e) => setFilter({ ...filter, possuiCarteira: e.target.value || null })}
        value={filter.possuiCarteira}
      />
    </Grid>
  ),

  useTipoUsuarioDocumento: ({ filter, setFilter, md = 4, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label="Tipo"
        showEmpty
        options={[
          { id: 'DOCUMENTO', nome: 'Documento' },
          { id: 'CERTIFICADO', nome: 'Certificado' },
        ]}
        onSelect={(e) => setFilter({ ...filter, tipo: e.target.value || null })}
        value={filter.tipo}
      />
    </Grid>
  ),

  usePossuiValidade: ({ filter, setFilter, md = 3, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Validade"
        onChange={(e) => setFilter({ ...filter, possuiValidade: e.target.value || null })}
        value={filter.possuiValidade}
      />
    </Grid>
  ),

  useValidade: ({ filter, setFilter, md = 3, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <NumberInputV2
        label="Validade (em anos)"
        onChange={(valor) => setFilter({ ...filter, validadeEmAnos: valor })}
        value={filter.validadeEmAnos}
        decimalScale={0}
        prefix=""
        thousandSeparator=""
      />
    </Grid>
  ),

  useGaragem: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Está na Garagem"
        onChange={(e) => setFilter({ ...filter, garagem: e.target.value || null })}
        value={filter.garagem}
      />
    </Grid>
  ),

  useColaborador: ({ filter, setFilter, md = 4, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <UsuarioSelectV2
        label="Colaborador"
        onSelect={(colaborador) => setFilter({ ...filter, colaborador })}
        value={filter.colaborador}
        ativo
      />
    </Grid>
  ),

  usePossuiRastreador: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Rastreador"
        onChange={(e) => setFilter({ ...filter, possuiRastreador: e.target.value || null })}
        value={filter.possuiRastreador}
      />
    </Grid>
  ),

  useNumRastreador: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. Rastreador"
        onChange={(e) => setFilter({ ...filter, numRastreador: e.target.value })}
        value={filter.numRastreador || ''}
      />
    </Grid>
  ),

  usePossuiCamera: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Camera"
        onChange={(e) => setFilter({ ...filter, possuiCamera: e.target.value || null })}
        value={filter.possuiCamera}
      />
    </Grid>
  ),

  useNumCamera: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. de Camera"
        onChange={(e) => setFilter({ ...filter, numCamera: e.target.value })}
        value={filter.numCamera || ''}
      />
    </Grid>
  ),

  useNumFrota: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. de Frota"
        onChange={(e) => setFilter({ ...filter, numFrota: e.target.value })}
        value={filter.numFrota || ''}
      />
    </Grid>
  ),

  useTipoEquipamento: ({ filter, setFilter, md = 3, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <TipoEquipamentoSelectV2
        showEmpty
        onChange={(tipoEquipamento) => setFilter({ ...filter, tipoEquipamento })}
        value={filter.tipoEquipamento}
      />
    </Grid>
  ),

  useNumPatrimonio: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. Patrimônio"
        onChange={(e) => setFilter({ ...filter, numPatrimonio: e.target.value })}
        value={filter.numPatrimonio || ''}
      />
    </Grid>
  ),

  useNumSerie: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. de Série"
        onChange={(e) => setFilter({ ...filter, numSerie: e.target.value })}
        value={filter.numSerie || ''}
      />
    </Grid>
  ),

  useSisant: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Código SISANT"
        onChange={(e) => setFilter({ ...filter, codigoSisant: e.target.value })}
        value={filter.codigoSisant || ''}
      />
    </Grid>
  ),

  useInspetorIds: ({ filter, setFilter, userRole, label = "Inspetor", md = 2, xs = 6 }) =>
    Role.INSPETOR !== userRole && (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label={label}
          onSelect={(inspetorIds) => setFilter({ ...filter, inspetorIds })}
          multiSelect
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={filter.inspetorIds}
        />
      </Grid>
    ),

  useEquipamentoOptions: ({
    filter,
    setFilter,
    label = "Equipamentos",
    tipoEquipamento = "DRONE",
    filterUsuarioUltimoRepasse = false,
    showUsuarioTodosRepasse = false,
    md = 2,
    xs = 6,
  }) => (
    <Grid size={{ md, xs }}>
      <EquipamentoSelectV2
        label={label}
        multiSelect
        tipoEquipamento={tipoEquipamento}
        filterUsuarioUltimoRepasse={filterUsuarioUltimoRepasse}
        showUsuarioTodosRepasse={showUsuarioTodosRepasse}
        value={filter.droneIds}
        onSelect={(droneIds) => setFilter({ ...filter, droneIds })}
      />
    </Grid>
  ),

  useCidade: ({ filter, setFilter, md = 2, xs = 6, label = "Cidade" }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={label}
        onChange={(e) => setFilter({ ...filter, cidade: e.target.value })}
        value={filter.cidade || ""}
      />
    </Grid>
  ),

  useValidate: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <ValidateSelectV2
        showEmpty
        onChange={(validate) =>
          setFilter({
            ...filter,
            validate,
          })
        }
        value={filter.validate}
      />
    </Grid>
  ),

  useCartao: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    multiSelect = false,
    key: filterFieldKey = 'cartao',
    label,
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <CartaoSelectV2
        label={label}
        multiSelect={multiSelect}
        disabled={disabled}
        onSelect={(value) =>
          setFilter({
            ...filter,
            [filterFieldKey]: value,
          })
        }
        value={filter[filterFieldKey]}
      />
    </Grid>
  ),

  useNome: ({ filter, setFilter, nomeLabel, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={nomeLabel || "Nome"}
        onChange={(e) => setFilter({ ...filter, nome: e.target.value })}
        value={filter.nome}
      />
    </Grid>
  ),

  useFornecedor: ({
    filter,
    setFilter,
    fornecedorLabel,
    label,
    md = 2,
    xs = 6,
    options,
    key = "fornecedor",
    multiSelect = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label || fornecedorLabel || "Fornecedor"}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
      />
    </Grid>
  ),

  useProduto: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    options,
    key = 'produto',
    label = 'Produto',
    multiSelect = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
      />
    </Grid>
  ),

  useLocal: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    key = 'local',
    label = 'Local',
    options,
    multiSelect = false,
  }) => {
    const localOptions = (options || [
      { id: 'Almoxarifado I  C17', nome: 'Almoxarifado I  C17' },
      { id: 'Almoxarifado II  C17', nome: 'Almoxarifado II  C17' },
      { id: 'Sala Drones', nome: 'Sala Drones' },
      { id: 'Sucata', nome: 'Sucata' },
    ]).map((o) => ({ value: o.id, label: o.nome }));

    if (multiSelect) {
      return (
        <Grid size={{ md, xs }}>
          <SearchSelectV2
            label={label}
            multiSelect
            options={localOptions}
            onSelect={(value) => setFilter({ ...filter, [key]: value })}
            value={filter[key]}
          />
        </Grid>
      );
    }

    return (
      <Grid size={{ md, xs }}>
        <DefaultSelectV2
          label={label}
          name={key}
          showEmpty
          options={options || [
            { id: 'Almoxarifado I  C17', nome: 'Almoxarifado I  C17' },
            { id: 'Almoxarifado II  C17', nome: 'Almoxarifado II  C17' },
            { id: 'Sala Drones', nome: 'Sala Drones' },
            { id: 'Sucata', nome: 'Sucata' },
          ]}
          onSelect={(e) => setFilter({ ...filter, [key]: e.target.value || null })}
          value={filter[key]}
        />
      </Grid>
    );
  },

  useAno: ({ filter, setFilter, md = 2, xs = 6, key = "ano", label = "Ano" }) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return (
      <Grid size={{ md, xs }}>
        <DefaultSelectV2
          label={label}
          name={key}
          showEmpty={false}
          options={years.map((y) => ({ id: String(y), nome: String(y) }))}
          onSelect={(e) => setFilter({ ...filter, [key]: e.target.value ? Number(e.target.value) : null })}
          value={filter[key] != null ? String(filter[key]) : String(currentYear)}
        />
      </Grid>
    );
  },

  usePedidoVooStatus: ({ filter, setFilter, md = 12, xs = 12, label = "Status" }) => {
    const options = [
      { value: "AGUARDANDO_VALIDACAO", label: "Aguardando Aprovação" },
      { value: "VALIDADO", label: "Aprovação" },
      { value: "INVALIDADO", label: "Negado" },
      { value: "EXPIRED", label: "Expirado" },
    ];
    return (
      <Grid size={{ md, xs }}>
        <MultiSelectV2
          label={label}
          onChange={(values) =>
            setFilter({
              ...filter,
              status: values ? values.map((v) => v.value) : [],
            })
          }
          defaultValue={
            filter.status && filter.status.length
              ? options.filter((o) => filter.status.indexOf(o.value) > -1)
              : []
          }
          options={options}
        />
      </Grid>
    );
  },
};

const CustomFilterV2 = ({
  fields = [],
  onFilter,
  userRole,
  produtoOptions,
  fornecedorOptions,
  loadProdutosOptions,
  loadPedidoFornecedorOptions,
  defaultFilter = {},
  appliedFilter,
  defaultCollapseValue = false,
  defaultCollapseUnmountOnExit = true,
  confirmButtonText = "Filtrar",
  variant = "card",
  open = false,
  onClose = null,
  title = "Filtros",
  maxWidth = "md",
  fullWidth = true,
}) => {
  const [filter, setFilter] = useState(() => cloneFilter(defaultFilter));
  const [filterFormKey, setFilterFormKey] = useState(0);
  const [expanded, setExpanded] = useState(defaultCollapseValue);
  const prevDialogOpen = useRef(false);

  const hasUseProduto = useMemo(
    () => fields.some(({ field }) => field === "useProduto"),
    [fields]
  );

  const hasUseFornecedor = useMemo(
    () => fields.some(({ field }) => field === "useFornecedor"),
    [fields]
  );

  const produtoSearchOptions = useMemo(
    () => (produtoOptions || []).map((p) => ({ value: p.id, label: p.nome })),
    [produtoOptions]
  );

  const fornecedorSearchOptions = useMemo(
    () => mapFornecedorOptions(fornecedorOptions),
    [fornecedorOptions]
  );

  useEffect(() => {
    if (hasUseProduto) {
      loadProdutosOptions();
    }
  }, [hasUseProduto, loadProdutosOptions]);

  useEffect(() => {
    if (hasUseFornecedor) {
      loadPedidoFornecedorOptions();
    }
  }, [hasUseFornecedor, loadPedidoFornecedorOptions]);

  useEffect(() => {
    if (variant === "dialog") {
      const justOpened = open && !prevDialogOpen.current;
      prevDialogOpen.current = open;
      if (!justOpened) {
        return;
      }
      const source =
        appliedFilter !== undefined && appliedFilter !== null
          ? appliedFilter
          : defaultFilter;
      setFilter(cloneFilter(source));
      return;
    }

    if (variant === "inline" && appliedFilter !== undefined && appliedFilter !== null) {
      setFilter(cloneFilter(appliedFilter));
    }
  }, [open, variant, appliedFilter, defaultFilter]);

  const renderedFields = useMemo(
    () =>
      fields.map(({ field, ...rest }, idx) => {
        const Renderer = fieldRenderers[field];
        if (!Renderer) return null;

        const rendererProps = {
          filter,
          setFilter,
          userRole,
          ...rest,
        };

        if (field === "useProduto" && !rendererProps.options) {
          rendererProps.options = produtoSearchOptions;
        }

        if (field === "useFornecedor" && !rendererProps.options) {
          rendererProps.options = fornecedorSearchOptions;
        }

        return (
          <React.Fragment key={idx}>
            {Renderer(rendererProps)}
          </React.Fragment>
        );
      }),
    [fields, filter, userRole, produtoSearchOptions, fornecedorSearchOptions]
  );

  const handleApplyFilter = () => {
    const currentFilter = { ...filter };
    if (!currentFilter.status) currentFilter.status = null;
    return onFilter(currentFilter);
  };

  const handleClearFilter = () => {
    setFilter(cloneFilter(defaultFilter));
    setFilterFormKey((key) => key + 1);
  };

  if (variant === "inline") {
    return (
      <Grid container spacing={1} key={filterFormKey}>
        {renderedFields}
      </Grid>
    );
  }

  if (variant === "dialog") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth}>
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "background.paper",
            borderColor: "divider",
            pt: 3,
          }}
        >
          <Grid container spacing={2} key={filterFormKey}>
            {renderedFields}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button color="primary" size="small" variant="outlined" onClick={handleClearFilter}>
            Limpar
          </Button>
          <Button color="primary" size="small" variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          <Button color="primary" size="small" variant="contained" onClick={handleApplyFilter}>
            {confirmButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        overflow: "inherit",
      }}
    >
      <CardHeader
        title={title}
        onClick={() => {
          if (defaultCollapseUnmountOnExit) setExpanded(!expanded);
        }}
        sx={{
          pt: 1.25,
          px: 2,
          pb: 0,
          cursor: "pointer",
        }}
        action={
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              if (defaultCollapseUnmountOnExit) setExpanded(!expanded);
            }}
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              ml: "auto",
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit={defaultCollapseUnmountOnExit}>
        <CardContent>
          <Grid container spacing={2} key={filterFormKey}>
            {renderedFields}
          </Grid>
        </CardContent>
        <Divider sx={{ bgcolor: "divider" }} />
        <CardActions sx={{ p: 2 }}>
          <Button color="primary" size="small" variant="contained" onClick={handleApplyFilter}>
            {confirmButtonText}
          </Button>
          <Button color="primary" size="small" variant="contained" onClick={handleClearFilter}>
            Limpar
          </Button>
        </CardActions>
      </Collapse>
    </Card>
  );
};

CustomFilterV2.propTypes = {
  fields: PropTypes.array,
  onFilter: PropTypes.func.isRequired,
  userRole: PropTypes.string,
  defaultFilter: PropTypes.object,
  /** Valores já aplicados na tela; ao abrir o dialog, o formulário sincroniza com este objeto. */
  appliedFilter: PropTypes.object,
  defaultCollapseValue: PropTypes.bool,
  defaultCollapseUnmountOnExit: PropTypes.bool,
  confirmButtonText: PropTypes.string,
  variant: PropTypes.oneOf(["card", "dialog", "inline"]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  fullWidth: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actions,
      loadProdutosOptions,
      loadPedidoFornecedorOptions,
    },
    dispatch
  );

const mapStateToProps = (state) => ({
  cacheFiltroNsir: selectors.getCacheFiltroNsir(state),
  userRole: selectorsAuth.getRole(state),
  produtoOptions: selectorsProduto.getProdutoOptions(state),
  fornecedorOptions: selectorsPedido.getPedidoFornecedorOptions(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomFilterV2);
